import {preserveTimePreference} from './dateTime.js';
import { rolePrivilegeDefaults, getPrivilegesForRoles } from '../config/privileges.js';
export { rolePrivilegeDefaults } from '../config/privileges.js';
import { MenuPage, MenuPageKey, Privilege, PrivilegeMap, Role, RoleDefinition, UserAccount, GoogleProfile, UserStatus } from '../types.js';
import { getRepositoryForEntity } from './dataAccessBoundary.js';
import { db, selectWorkspaceAccount } from './db.js';
import { readApiResponse } from './apiResponse.js';

const USERS_KEY = 'sonata_users';
const ROLES_KEY = 'sonata_roles';
const CURRENT_USER_KEY = 'sonata_current_user_email';
const ACCOUNT_SESSION_KEY = 'sonata_account_session_v2';
const readAccountSession = (): { email: string | null; token: string | null } | null => {
  try { const raw = localStorage.getItem(ACCOUNT_SESSION_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const getEnv = (key: string): string | undefined => {
  try {
    if (typeof process !== 'undefined' && process.env && typeof process.env[key] === 'string') {
      return process.env[key] as string;
    }
  } catch {}
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any)?.env && typeof (import.meta as any).env[key] === 'string') {
      return (import.meta as any).env[key] as string;
    }
  } catch {}
  return undefined;
};

const GOOGLE_CLIENT_ID = getEnv('VITE_GOOGLE_CLIENT_ID');
const API_BASE = getEnv('VITE_API_URL') || '';
const SESSION_TOKEN_KEY = 'sonata_session_token';
const LEGACY_ID_TOKEN_KEY = 'sonata_id_token';
const POST_LOGIN_PATH_KEY = 'post_login_path';
const POST_LOGOUT_MAP_KEY = 'sonata_post_logout_map';
const AUTH_STATE_KEY = 'sonata_auth_state';
const AUTH_NONCE_KEY = 'sonata_auth_nonce';
const MIGRATION_FLAG_KEY = 'sonata_users_roles_migrated_v1';

if (typeof window !== 'undefined') {
  console.info('[auth] Resolved Google Client ID', GOOGLE_CLIENT_ID || '(missing)', { origin: window.location.origin });
}

const makeGuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
};

const nowIso = () => new Date().toISOString();

export const MENU_PAGES: MenuPage[] = [
  { key: 'map', label: 'Map Explorer', path: '/', category: 'Explore', description: 'Geospatial view of farms and tracks' },
  { key: 'places', label: 'Places', path: '/map-features', category: 'Management', description: 'Manage imported GPX data and map annotations' },
  { key: 'overview', label: 'Farm Dashboard', path: '/overview', category: 'Management' },
  { key: 'scouting', label: 'Gallery', path: '/gallery', category: 'Explore' },
  { key: 'projects', label: 'Projects', path: '/projects', category: 'Management' },
  { key: 'assets', label: 'Assets', path: '/assets', category: 'Management' },
  { key: 'documents', label: 'Documents', path: '/documents', category: 'Administration' },
  { key: 'settings', label: 'Settings', path: '/settings', category: 'Administration' },
];

const baseMenuPrivileges = MENU_PAGES.reduce((acc, page) => {
  acc[page.key] = { view: true, write: true };
  return acc;
}, {} as Record<MenuPageKey, Privilege>);

const adminPrivileges: PrivilegeMap = {
  ...baseMenuPrivileges,
  gpsTracking: { view: true, write: true },
  contributionReview: {view:true,write:true},
  communityParticipation: {view:true,write:true},
  photoModeration: {view:true,write:true},
  neighborContacts: {view:true,write:true},
};

const readonlyPrivilege: Privilege = { view: true, write: false };

const guestPrivileges = MENU_PAGES.reduce((acc, page) => {
  acc[page.key] = { view: false, write: false };
  return acc;
}, { gpsTracking: { view: false, write: false } } as PrivilegeMap);

const ADMIN_EMAIL = 'gratefulcubs@gmail.com';
const ROLE_ORDER: Role[] = ['Admin', 'Manager', 'Worker', 'Hunter', 'Family', 'Friends', 'VIP', 'Guest'];

const sortRoleDefinitions = (roles: RoleDefinition[]) => {
  return [...roles].sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role));
};

const clonePrivileges = (privs: PrivilegeMap): PrivilegeMap => {
  return Object.entries(privs).reduce((acc, [key, value]) => {
    acc[key as keyof PrivilegeMap] = { ...(value as Privilege) };
    return acc;
  }, {} as PrivilegeMap);
};

const mergePrivilegesWithDefaults = (defaults: PrivilegeMap, overrides?: Partial<PrivilegeMap>): PrivilegeMap => {
  const merged = clonePrivileges(defaults);
  if (!overrides) return merged;

  (Object.keys(overrides) as (keyof PrivilegeMap)[]).forEach((key) => {
    const override = overrides[key];
    if (!override) return;
    merged[key] = {
      ...merged[key],
      ...override,
    };
  });

  return merged;
};

const AUTH_EVENT = 'sonata-auth-changed';
const AUTH_LIST_CACHE_TTL_MS = 10_000;

const canAttemptAuthenticatedRequests = () => {
  if (typeof sessionStorage === 'undefined') return true;
  return !!getAuthToken();
};

let usersCache: { value: UserAccount[]; expiresAt: number } | null = null;
let usersInFlight: Promise<UserAccount[]> | null = null;
let rolesCache: { value: RoleDefinition[]; expiresAt: number } | null = null;
let rolesInFlight: Promise<RoleDefinition[]> | null = null;

const invalidateAuthListCaches = () => {
  usersCache = null;
  rolesCache = null;
};

export const broadcastAuthChange = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_EVENT));
};

export const setAccountSession = (email: string | null, token: string | null) => {
  const normalized = email?.trim().toLowerCase() || null;
  const outgoing=readAccountSession();
  if(outgoing?.email&&outgoing.email!==normalized)revokeDevicePush(outgoing.token,API_BASE);
  // Publish identity and credential together so another tab never pairs a new
  // account with the previous account's token.
  localStorage.setItem(ACCOUNT_SESSION_KEY, JSON.stringify({ email:normalized, token }));
  selectWorkspaceAccount(normalized);
  invalidateAuthListCaches();
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(LEGACY_ID_TOKEN_KEY);
  localStorage.removeItem(CURRENT_USER_KEY);
  if (typeof sessionStorage !== 'undefined') { sessionStorage.removeItem(SESSION_TOKEN_KEY); sessionStorage.removeItem(LEGACY_ID_TOKEN_KEY); }
  broadcastAuthChange();
};
export const setStoredIdToken = (token: string | null) => {
  if (typeof localStorage === 'undefined') return;
  setAccountSession(getCurrentUserEmail(), token);
};

export const getAuthToken = (): string | null => {
  const session = readAccountSession();
  if (session) return session.token;
  if (typeof localStorage !== 'undefined') {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (token) return token;
    const legacy = localStorage.getItem(LEGACY_ID_TOKEN_KEY);
    if (legacy) {
      localStorage.removeItem(LEGACY_ID_TOKEN_KEY);
    }
  }
  if (typeof sessionStorage === 'undefined') return null;
  const token = sessionStorage.getItem(SESSION_TOKEN_KEY);
  if (token && typeof localStorage !== 'undefined') {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  }
  const legacy = sessionStorage.getItem(LEGACY_ID_TOKEN_KEY);
  if (legacy) {
    sessionStorage.removeItem(LEGACY_ID_TOKEN_KEY);
  }
  return token;
};

export const setPostLoginPath = (path: string) => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(POST_LOGIN_PATH_KEY, path);
};

export const consumePostLoginPath = () => {
  if (typeof sessionStorage === 'undefined') return '/';
  const stored = sessionStorage.getItem(POST_LOGIN_PATH_KEY) || '/';
  sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
  return stored;
};

const getUserRepo = () => getRepositoryForEntity<UserAccount>('users');
const getRoleRepo = () => getRepositoryForEntity<RoleDefinition>('roleDefinitions');

const loadUserByEmail = async (email: string): Promise<UserAccount | null> => {
  if (!canAttemptAuthenticatedRequests()) return null;
  try {
    const repo = getUserRepo();
    const user = await repo.get(email);
    return user ? normalizeUser(user) : null;
  } catch {
    return null;
  }
};

const withLocalStorage = <T>(fn: (ls: Storage) => T): T | null => {
  try {
    if (typeof localStorage === 'undefined') return null;
    return fn(localStorage);
  } catch {
    return null;
  }
};

const hasMigrationFlag = () => withLocalStorage((ls) => ls.getItem(MIGRATION_FLAG_KEY)) != null;
const markMigrationFlag = () => withLocalStorage((ls) => ls.setItem(MIGRATION_FLAG_KEY, nowIso()));

const parseLegacyUsers = (): UserAccount[] => {
  const raw = withLocalStorage((ls) => ls.getItem(USERS_KEY));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as UserAccount[];
    if (Array.isArray(parsed)) return parsed;
  } catch (err) {
    console.warn('[auth] Failed to parse legacy users', err);
  }
  return [];
};

const parseLegacyRoles = (): RoleDefinition[] => {
  const raw = withLocalStorage((ls) => ls.getItem(ROLES_KEY));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as RoleDefinition[];
    if (Array.isArray(parsed)) return parsed;
  } catch (err) {
    console.warn('[auth] Failed to parse legacy roles', err);
  }
  return [];
};

const scrubNullString = <T>(value: T): T | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string' && value.toLowerCase() === 'null') return undefined;
  return value;
};

const scrubDate = (value?: string | null) => scrubNullString(value);

const pickDate = (user: any, keys: string[]) => {
  for (const key of keys) {
    const val = scrubDate(user?.[key]);
    if (val) return val;
  }
  return undefined;
};

const normalizeUser = (user: UserAccount): UserAccount => {
  const invitedAt = pickDate(user as any, ['invited_at', 'invitedAt']) || (user.status === 'Invited' ? nowIso() : undefined);
  const createdAt = pickDate(user as any, ['created_at']) || nowIso();
  const updatedAt = pickDate(user as any, ['updated_at']) || nowIso();
  const deletedAt = scrubDate((user as any).deleted_at);

  const normalized: any = {
    ...user,
    email: user.email.trim().toLowerCase(),
    status: user.status || 'Active',
    invited_at: invitedAt,
    server_version: user.server_version ?? 0,
    created_at: createdAt,
    updated_at: updatedAt,
    deleted_at: deletedAt,
  };

  // Remove any lingering "null" string fields that could break server inserts
  Object.entries(normalized).forEach(([key, value]) => {
    if (value === 'null') {
      normalized[key] = undefined;
    }
  });

  // Remove camelCase/snake-less variants so server payload only contains canonical columns
  delete normalized.invitedAt;
  delete normalized.invitedat;
  delete normalized.createdAt;
  delete normalized.updatedAt;
  delete normalized.deletedAt;

  return normalized;
};

const normalizeRoleDefinition = (roleDef: RoleDefinition): RoleDefinition => ({
  ...roleDef,
  privileges: getPrivilegesForRoles([roleDef.role], [roleDef]),
  server_version: roleDef.server_version ?? 0,
  created_at: roleDef.created_at || nowIso(),
  updated_at: roleDef.updated_at || nowIso(),
});

const seedAdminUser = async (existing: UserAccount[] = []): Promise<UserAccount[]> => {
  const hasAdmin = existing.some((u) => u.email === ADMIN_EMAIL);
  if (hasAdmin) return existing.map(normalizeUser);
  const repo = getUserRepo();
  const admin: UserAccount = normalizeUser({
    email: ADMIN_EMAIL,
    name: 'Admin',
    role: 'Admin',
    status: 'Active',
    invitedAt: nowIso(),
    picture: undefined,
    server_version: 0,
  });
  await repo.upsert(admin);
  return [...existing, admin].map(normalizeUser);
};

const seedRoleDefinitions = async (existing: RoleDefinition[] = []): Promise<RoleDefinition[]> => {
  if (existing.length) return existing.map(normalizeRoleDefinition);
  const repo = getRoleRepo();
  const seeded: RoleDefinition[] = (Object.keys(rolePrivilegeDefaults) as Role[]).map((role) =>
    normalizeRoleDefinition({ role, privileges: clonePrivileges(rolePrivilegeDefaults[role]), server_version: 0 })
  );
  await repo.bulkUpsert(seeded);
  return seeded;
};

export const migrateLegacyAuthStorage = async () => {
  if (hasMigrationFlag()) return;
  const legacyUsers = parseLegacyUsers();
  const legacyRoles = parseLegacyRoles();
  if (!legacyUsers.length && !legacyRoles.length) return;

  const userRepo = getUserRepo();
  const roleRepo = getRoleRepo();

  if (legacyUsers.length) {
    const normalized = legacyUsers.map(normalizeUser);
    try {
      await userRepo.bulkUpsert(normalized);
      withLocalStorage((ls) => ls.removeItem(USERS_KEY));
    } catch (err) {
      console.error('[auth] Failed migrating legacy users', err);
    }
  }

  if (legacyRoles.length) {
    const normalizedRoles = legacyRoles.map(normalizeRoleDefinition);
    try {
      await roleRepo.bulkUpsert(normalizedRoles);
      withLocalStorage((ls) => ls.removeItem(ROLES_KEY));
    } catch (err) {
      console.error('[auth] Failed migrating legacy roles', err);
    }
  }

  markMigrationFlag();
};

export const loadUsers = async (): Promise<UserAccount[]> => {
  const database = db;
  const profile = await database.workspaceMeta.get('profile');
  const saved = profile?.value ? [profile.value] : [];
  if (!canAttemptAuthenticatedRequests() || typeof navigator !== 'undefined' && !navigator.onLine) return saved;
  if (usersCache && usersCache.expiresAt > Date.now()) return usersCache.value;
  if (usersInFlight) return usersInFlight;
  const promise = (async () => {
    const me = await fetchCurrentAccount();
    let fetched:UserAccount[]=[me];
    if(me.roles?.includes('Admin')&&me.status==='Active'){
      // Workspace sync intentionally retains only the current profile. The
      // administrative directory must read the authorized live endpoint.
      const token=getAuthToken();
      const response=await fetch(API_BASE+'/api/users',{headers:{Authorization:'Bearer '+token}});
      const value=await readApiResponse(response);
      if(database!==db||token!==getAuthToken())throw new Error('Account changed. Please refresh the people list.');
      if(response.status===401&&token)clearAuthSession();
      if(!response.ok)throw new Error(value.error||'Could not load the people list.');
      fetched=(Array.isArray(value)?value:[]).map(normalizeUser);
    }
    if (db !== database) return [];
    usersCache = { value:fetched, expiresAt:Date.now() + AUTH_LIST_CACHE_TTL_MS };
    return fetched;
  })();
  usersInFlight = promise;
  try { return await promise; }
  catch (error) { if (saved.length) return saved; throw error; }
  finally { if (usersInFlight === promise) usersInFlight = null; }
};

export const loadRoleDefinitions = async (): Promise<RoleDefinition[]> => {
  const database = db;
  const cached = await database.workspaceMeta.get('roles');
  const defaults = () => sortRoleDefinitions((Object.keys(rolePrivilegeDefaults) as Role[]).map(role => normalizeRoleDefinition({role,privileges:clonePrivileges(rolePrivilegeDefaults[role]),server_version:0})));
  if (!canAttemptAuthenticatedRequests() || typeof navigator !== 'undefined' && !navigator.onLine) return cached?.value || defaults();
  if (rolesCache && rolesCache.expiresAt > Date.now()) return rolesCache.value;
  if (rolesInFlight) return rolesInFlight;
  const promise = (async () => {
    const sorted = sortRoleDefinitions((await getRoleRepo().list()).map(normalizeRoleDefinition));
    if (db !== database) return [];
    await database.workspaceMeta.put({key:'roles',value:sorted});
    rolesCache = {value:sorted,expiresAt:Date.now() + AUTH_LIST_CACHE_TTL_MS};
    return sorted;
  })();
  rolesInFlight = promise;
  try { return await promise; }
  catch (error) { if (cached?.value) return cached.value; throw error; }
  finally { if (rolesInFlight === promise) rolesInFlight = null; }
};

export const setRolePrivileges = async (role: Role, privileges: PrivilegeMap) => {
  const repo = getRoleRepo();
  const now = nowIso();
  const current = await repo.get(role);
  const payload = normalizeRoleDefinition({
    role,
    privileges: clonePrivileges(privileges),
    created_at: current?.created_at || now,
    server_version: current?.server_version ?? 0,
    updated_at: now,
  });
  await repo.upsert(payload);
  invalidateAuthListCaches();
};

export const resolveRolePrivileges = (role: Role, roleDefinitions: RoleDefinition[]): PrivilegeMap => {
  if (role === 'Admin') return clonePrivileges(adminPrivileges);
  const defaults = rolePrivilegeDefaults[role] || rolePrivilegeDefaults.Guest;
  const def = roleDefinitions.find((r) => r.role === role);
  if (def) return mergePrivilegesWithDefaults(defaults, def.privileges);
  return clonePrivileges(defaults);
};

export const getRolePrivileges = async (role: Role): Promise<PrivilegeMap> => {
  const roles = await loadRoleDefinitions();
  return resolveRolePrivileges(role, roles);
};

export const setCurrentUserEmail = (email: string | null) => {
  const normalized = email?.trim().toLowerCase() || null;
  const sameAccount = normalized === getCurrentUserEmail()?.trim().toLowerCase();
  setAccountSession(normalized, sameAccount ? getAuthToken() : null);
};

export const getCurrentUserEmail = (): string | null => readAccountSession() ? readAccountSession()!.email : withLocalStorage((ls) => ls.getItem(CURRENT_USER_KEY)) || null;

export const upsertUser = async (user: UserAccount) => {
  const repo = getUserRepo();
  const now = nowIso();
  const existing = user.email ? await repo.get(user.email) : undefined;
  const payload = normalizeUser({
    ...existing,
    ...user,
    created_at: existing?.created_at || now,
    server_version: existing?.server_version ?? 0,
    updated_at: now,
  });
  const saved = await repo.upsert(payload);
  invalidateAuthListCaches();
  return saved;
};

export const deleteUser = async (email: string) => {
  if (email === ADMIN_EMAIL) return;
  const repo = getUserRepo();
  await repo.remove(email);
  invalidateAuthListCaches();
};

export const permanentlyDeleteUser = async (email: string, confirmation: {confirmation_email:string;revision:string;transfer_email:string}) => {
  const token=getAuthToken(),database=db;
  const response=await fetch(API_BASE+'/api/users/'+encodeURIComponent(email)+'/delete-permanently',{
    method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},body:JSON.stringify(confirmation),
  });
  const payload=await readApiResponse(response);
  if(database!==db||token!==getAuthToken())throw new Error('Account changed. Please refresh the people list.');
  if(response.status===401&&token)clearAuthSession();
  if(!response.ok)throw Object.assign(new Error(payload.error||'Could not delete this account.'),{status:response.status});
  invalidateAuthListCaches();
  return payload as {ok:boolean;transferred_photos:number};
};

const createUserFromProfile = (profile: GoogleProfile, role: Role = 'Guest', status: UserStatus = 'Active'): UserAccount => ({
  email: profile.email,
  name: profile.name || profile.email.split('@')[0],
  picture: profile.picture,
  role,
  status,
  invitedAt: status === 'Invited' ? nowIso() : undefined,
  server_version: 0,
});

const ensureGoogleScript = () => {
  if (document.getElementById('google-identity')) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-identity';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services script'));
    document.body.appendChild(script);
  });
};

const assertClientId = () => {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google Client ID missing. Set VITE_GOOGLE_CLIENT_ID in environment.');
  }
};

export const hasGoogleClientId = () => true;

const decodeJwtPayload = (token: string): any => {
  const [, payload] = token.split('.');
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const json = atob(normalized);
  return JSON.parse(json);
};

const persistNonceAndState = () => {
  const nonce = makeGuid();
  const state = makeGuid();
  sessionStorage.setItem(AUTH_NONCE_KEY, nonce);
  sessionStorage.setItem(AUTH_STATE_KEY, state);
  return { nonce, state };
};

const validateStateAndClear = (state?: string | null) => {
  const expected = sessionStorage.getItem(AUTH_STATE_KEY);
  sessionStorage.removeItem(AUTH_STATE_KEY);
  if (!expected) return true;
  return expected === state;
};

const validateNonce = (nonce?: string | null) => {
  const expected = sessionStorage.getItem(AUTH_NONCE_KEY);
  sessionStorage.removeItem(AUTH_NONCE_KEY);
  if (!expected) return true;
  return expected === nonce;
};

const beginGoogleRedirectWithPrompt = async (prompt?: string) => {
  assertClientId();
  await ensureGoogleScript();
  const path = window.location.pathname + window.location.search + window.location.hash;
  const afterSignOut = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(POST_LOGOUT_MAP_KEY) === '1';
  setPostLoginPath(afterSignOut || window.location.pathname === '/welcome' ? '/' : path);
  if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(POST_LOGOUT_MAP_KEY);
  const { nonce, state } = persistNonceAndState();
  const redirectUri = `${window.location.origin}/auth/callback`;
  const normalizedPrompt = prompt?.trim();
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID || '',
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: 'openid email profile',
    state,
    nonce,
    response_mode: 'fragment',
  });
  if (normalizedPrompt) {
    params.set('prompt', normalizedPrompt);
  }
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
};

// Explicit sign-in must offer an account choice even when Google retains a
// browser session after the user signs out of Sonata Fields.
export const beginGoogleRedirect = async () => beginGoogleRedirectWithPrompt('select_account');

const exchangeGoogleIdTokenForSession = async (idToken: string): Promise<{ token: string }> => {
  const response = await fetch(`${API_BASE}/auth/session/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (!response.ok) {
    throw new Error('Unable to establish application session');
  }
  const data = await response.json();
  if (!data?.token) {
    throw new Error('Session token missing from exchange response');
  }
  return data as { token: string };
};

export const completeRedirectSignIn = async (idToken: string, state?: string | null, nonce?: string | null): Promise<UserAccount> => {
  if (!validateStateAndClear(state)) {
    throw new Error('Sign-in response state mismatch');
  }
  const payload = decodeJwtPayload(idToken);
  if (!validateNonce(nonce || payload?.nonce)) {
    throw new Error('Sign-in response nonce mismatch');
  }
  if (!payload?.email) {
    throw new Error('Missing email from Google ID token');
  }
  const session = await exchangeGoogleIdTokenForSession(idToken);
  setAccountSession(payload.email, session.token);
  const user = await fetchCurrentAccount();
  await db.workspaceMeta.put({key:'profile',value:user});
  setCurrentUserEmail(user.email);
  return user;
};

export const signOut = () => {
  setAccountSession(null,null);
  // Explicit sign-out starts the next session at the field map. Authentication
  // recovery from an intentional content link can still return to that link.
  try {
    sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
    sessionStorage.setItem(POST_LOGOUT_MAP_KEY, '1');
  } catch {}
};

let renewSessionInFlight: Promise<boolean> | null = null;
let renewalKey: string | null = null;
export const attemptSessionRenewal = async (): Promise<boolean> => {
  const token = getAuthToken(), database = db;
  if (!token) return false;
  const key = database.name + ':' + token;
  if (renewSessionInFlight && renewalKey === key) return renewSessionInFlight;
  renewalKey = key;
  renewSessionInFlight = (async () => {
    try {
      const response = await fetch(API_BASE + '/auth/session/renew', { method:'POST', headers:{ Authorization:'Bearer ' + token } });
      if (response.status === 401) {
        if (database === db && getAuthToken() === token) clearAuthSession();
        return false;
      }
      if (!response.ok) throw new Error('Sign-in service temporarily unavailable. Your saved session is retained.');
      const payload = await response.json();
      if (!payload?.token || database !== db || getAuthToken() !== token) return false;
      setStoredIdToken(payload.token);
      return true;
    }
    finally { if (renewalKey === key) { renewSessionInFlight = null; renewalKey = null; } }
  })();
  return renewSessionInFlight;
};

export const clearAuthSession = () => {
  // Expired server credentials must not erase the offline workspace identity.
  // Explicit Sign out still closes it through signOut().
  setStoredIdToken(null);
};

export const resolveCurrentUser = async (revalidate = false): Promise<UserAccount | null> => {
  const email = getCurrentUserEmail()?.trim().toLowerCase();
  if (!email) {
    return null;
  }

  const repo = getUserRepo();
  const accountDb = db;
  const cached = await accountDb.workspaceMeta.get('profile');
  if (cached?.value?.email === email && (!revalidate || typeof navigator !== 'undefined' && !navigator.onLine || !getAuthToken())) return cached.value;
  if (!canAttemptAuthenticatedRequests()) return null;
  try {
    const user = await fetchCurrentAccount();
    if (user) {
      let normalized = normalizeUser(user);
      await accountDb.transaction('rw',accountDb.workspaceMeta,async()=>{
        const latest=await accountDb.workspaceMeta.get('profile');
        normalized=preserveTimePreference(latest?.value,normalized);
        await accountDb.workspaceMeta.put({key:'profile',value:normalized});
      });
      return normalized;
    }
    await accountDb.workspaceMeta.delete('profile');
    return null;
  } catch (err) {
    console.warn('[auth] Failed to resolve current user from repository', err);
    if (cached?.value?.email === email) return cached.value;
  }

  return null;
};

export const resolvePrivilegesForUser = (
  user: UserAccount | null,
  roleDefinitions: RoleDefinition[] = []
): PrivilegeMap => {
  if (!user || user.status !== 'Active') return getPrivilegesForRoles([]);
  if (user.privileges) return user.privileges;
  return getPrivilegesForRoles(user.roles || [user.role], roleDefinitions);
};

export const inviteUser = async (email: string, role: Role | Role[], farmIds: string[] = []): Promise<UserAccount> => {
  const existing = (await loadUsers()).find((u) => u.email === email);
  if (existing) return existing;
  const roles = Array.isArray(role) ? role : [role];
  const user = {...createUserFromProfile({ email }, roles[0], 'Invited'), roles, farm_ids:farmIds};
  const saved = await upsertUser(user);
  return saved;
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === ACCOUNT_SESSION_KEY || event.key === null || !readAccountSession() && (event.key === CURRENT_USER_KEY || event.key === SESSION_TOKEN_KEY)) {
      selectWorkspaceAccount(getCurrentUserEmail());
      invalidateAuthListCaches();
      broadcastAuthChange();
    }
  });
}

export async function fetchCurrentAccount(): Promise<UserAccount> {
  const token = getAuthToken(), database = db;
  const response = await fetch(API_BASE + '/api/guest/me', {headers:{Authorization:'Bearer '+token}});
  const data = await readApiResponse(response);
  if (database !== db || token !== getAuthToken()) throw new Error('Account changed. Please try again.');
  if (response.status === 401 && token) clearAuthSession();
  if (!response.ok) throw new Error(data.error || 'Could not verify your account access. Please retry.');
  return data;
}
import {revokeDevicePush} from './pushRevocation.js';
